import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";
import { Box, Typography } from "@mui/material";

const hasMeaningfulIncomeExpenses = (data) => {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.some((item) => {
    const income = Number(item?.income) || 0;
    const expenses = Number(item?.expenses) || 0;
    return income !== 0 || expenses !== 0;
  });
};

const EmptyState = ({ height = 160 }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height,
      minHeight: 100,
      borderRadius: "8px",
      bgcolor: "#f8fafc",
      border: "1px dashed #e2e8f0",
      px: 2,
    }}
  >
    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
      No data available
    </Typography>
  </Box>
);

const LEGEND_ITEMS = [
  { key: "income", label: "Income", color: "#2563eb" },
  { key: "expenses", label: "Expenses", color: "#16a34a" },
];

/** Legend + "Monthly" period pill for the card header row */
export const IncomeExpensesChartLegend = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", flexShrink: 0 }}>
    {LEGEND_ITEMS.map((item) => (
      <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: item.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", lineHeight: 1 }}>
          {item.label}
        </Typography>
      </Box>
    ))}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.4,
        px: 1,
        py: 0.35,
        border: "1px solid #e2e8f0",
        borderRadius: "6px",
        bgcolor: "#fff",
      }}
    >
      <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#334155" }}>Monthly</Typography>
      <Box component="span" sx={{ fontSize: "0.55rem", color: "#94a3b8", lineHeight: 1 }}>▾</Box>
    </Box>
  </Box>
);

const formatLakh = (thousands) => {
  const value = Number(thousands) || 0;
  const lakhs = value / 100; // data is stored in ₹ thousands; 100K = 1L
  return `₹${lakhs.toFixed(2)}L`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.98)",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "8px 10px",
        boxShadow: "0 6px 16px rgba(15,23,42,0.1)",
        color: "#0f172a",
        fontSize: "12px",
        minWidth: "140px",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((entry, index) => (
        <div
          key={index}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}
        >
          <span style={{ color: entry.color, fontWeight: 600 }}>
            {entry.dataKey === "income" ? "Income" : "Expenses"}:
          </span>
          <span style={{ fontWeight: 700, marginLeft: 8 }}>{formatLakh(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const CurrentMonthTick = ({ x, y, payload, currentMonth }) => {
  const isCurrent = payload.value === currentMonth;
  return (
    <text
      x={x}
      y={y + 12}
      textAnchor="middle"
      fontSize={9}
      fontWeight={isCurrent ? 800 : 600}
      fill={isCurrent ? "#2563eb" : "#64748b"}
    >
      {payload.value}
    </text>
  );
};

const IncomeExpensesChart = ({ data, height = 300, currentMonth }) => {
  if (!hasMeaningfulIncomeExpenses(data)) {
    return <EmptyState height={typeof height === "number" ? height : 140} />;
  }

  const chart = (
    <BarChart data={data} margin={{ top: 6, right: 8, left: 4, bottom: 0 }} barGap={2}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
      {currentMonth ? (
        <ReferenceArea
          x1={currentMonth}
          x2={currentMonth}
          fill="#eff6ff"
          fillOpacity={1}
          ifOverflow="visible"
        />
      ) : null}
      <XAxis
        dataKey="month"
        axisLine={false}
        tickLine={false}
        tick={<CurrentMonthTick currentMonth={currentMonth} />}
        tickMargin={2}
        interval={0}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={{ fill: "#94a3b8", fontSize: 9 }}
        tickMargin={4}
        width={34}
        tickFormatter={(v) => `${Math.round((Number(v) || 0) / 100)}L`}
      />
      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37, 99, 235, 0.06)" }} />
      <Bar dataKey="income" name="income" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={16} isAnimationActive={false} />
      <Bar dataKey="expenses" name="expenses" fill="#16a34a" radius={[3, 3, 0, 0]} maxBarSize={16} isAnimationActive={false} />
    </BarChart>
  );

  const chartBody = (
    <Box sx={{ position: "absolute", inset: 0, pl: "6px", pr: "8px" }}>
      <ResponsiveContainer width="100%" height="100%" debounce={100}>
        {chart}
      </ResponsiveContainer>
    </Box>
  );

  if (height === "100%") {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 140, position: "relative" }}>
        {chartBody}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height, position: "relative" }}>
      {chartBody}
    </Box>
  );
};

export default React.memo(IncomeExpensesChart);
